// src/utils/faxReasons.js
// Fax failure reasons in plain words, with "why this happened and what to
// do" (3.57.7, owner): the same text as the failure emails. The backend keeps
// the one list (lib/faxReasonLabel.js); the portal loads it once from
// /portal/fax-reasons. Until then, a code reads as words
// ("receiver_call_dropped" → "Receiver call dropped").
import { API_BASE } from '../firebase/config';

let data = null, loading = null;

export function loadFaxReasons(getToken) {
  if (data || loading) return loading;
  loading = (async () => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/portal/fax-reasons`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) data = await r.json();
    } catch { /* tried again next time */ }
    finally { loading = null; }
  })();
  return loading;
}

const words = r => {
  const s = String(r || '').trim();
  if (!s) return '';
  if (/^[a-z0-9_]+$/.test(s)) { const w = s.replace(/_/g, ' '); return w[0].toUpperCase() + w.slice(1); }
  return s;
};
const code = r => String(r || '').trim();

// 3.67.4 (owner): `history` = the fax's failureContext, the number's history
// ('delivered' | 'first' | 'never'): "call dropped" and "no fax machine
// answered" only suggest a voice line when no fax to it has got through
// "The call dropped before the fax finished"
export function reasonText(reason, history = null) {
  const c = code(reason);
  if (history === 'delivered' && data?.labelWhenDelivered?.[c]) return data.labelWhenDelivered[c];
  return data?.labels?.[c] || words(reason) || 'Delivery failed';
}
// "The receiving fax machine answered but hung up … Wait a few minutes and try again."
export function reasonWhy(reason, history = null) {
  const c = code(reason);
  if (history && data?.voiceCodes?.includes(c) && data?.history?.[history]) return data.history[history];
  return data?.explanations?.[c] || data?.fallback || '';
}

// 3.62.23 (owner-approved): a fax that failed to ARRIVE, in words from our
// side (the backend's INCOMING list and patterns); faxes we send keep the
// wording above
const IN_FALLBACK = { label: "The fax didn't come through", why: "The call ended before the fax arrived. If you're expecting it, ask the sender to send it again." };
function incomingEntry(reason) {
  const r = code(reason);
  for (const [kind, re] of data?.incomingPatterns || []) if (new RegExp(re, 'i').test(r)) return data.incoming[kind];
  return data?.incoming?.other || IN_FALLBACK;
}
export const incomingReasonText = reason => incomingEntry(reason).label;
export const incomingReasonWhy = reason => incomingEntry(reason).why;

// The "failed again" window's "What to do" (owner-approved wording)
const WRONG_NUMBER = ['destination_invalid', 'receiver_unallocated_number'];
const RECEIVER_SIDE = ['receiver_call_dropped', 'sender_call_dropped', 'fax_initial_communication_timeout', 'fax_signaling_error',
  'receiver_decline', 'invalid_ecm_response_from_receiver', 'carrier_lost', 'user_busy'];
export function repeatAdvice(reason) {
  const c = code(reason);
  if (WRONG_NUMBER.includes(c)) return 'Check the fax number and try again.';
  if (RECEIVER_SIDE.includes(c)) return 'Please phone the receiver to check their fax machine and number before sending again. Each failed attempt is still a call to their line.';
  return null;   // the explanation above already says what to do
}
